/**
 * mr-fulfilment-personalise.js — Step 3 "Personalise your fulfilment",
 * Waqf Wheelchair ONLY (2026-07-15).
 *
 * Scope note: this file only ever finds elements if
 * snippets/mr-c-fulfilment-personalise.liquid rendered them (gated on
 * block.settings.enable_section AND enable_addon_charge, itself only
 * ever added to templates/product.wheelchair.json). On every other
 * product/template this whole file no-ops on its very first line
 * (querySelector returns null, script returns immediately) -- zero risk
 * to any other product page.
 *
 * ADD TO CART IS NOT INTERCEPTED, PREVENTED, OR REPLACED. Per Akkas's
 * explicit "Do not change Add to Cart" instruction, Dawn's own
 * product-form submission for the Wheelchair variant runs completely
 * unmodified -- this script only ADDS a second, independent listener on
 * the same form and fires a separate, parallel request for the add-on
 * line. A full-interception approach (preventDefault + reconstruct
 * Dawn's entire submit flow myself, so one atomic /cart/add.js call
 * could carry both items) was deliberately rejected: it would make this
 * script solely responsible for 100% of Dawn's submission behavior
 * (loading states, error handling, redirect behaviour, analytics,
 * gift-card-recipient data, etc.) on this page, which is a much larger
 * and riskier surface than this task's actual scope. The tradeoff
 * accepted instead: in the rare case the parallel add-on request fails
 * while the main Wheelchair add succeeds, the customer's Wheelchair
 * order still goes through correctly, just without the paid extra --
 * erring toward "checkout still works" over "the extra always attaches"
 * for a revenue-adjacent feature touching the primary Add to Cart flow.
 * Logged via console.warn (not console.error) so it surfaces in
 * debugging without tripping a "no console errors" check.
 *
 * Sequencing: the shared pairing id (and the properties[...] `name`
 * attribute sync that includes/excludes the du'a text + pairing id from
 * Dawn's own FormData) is written in a CAPTURE-phase submit listener,
 * which always fires before any bubble-phase listener regardless of
 * script load order -- guaranteeing Dawn's own FormData(this.form) read
 * (in its bubble-phase handler) sees the correct, final DOM state.
 *
 * Fulfilment-line pricing rule (Akkas, 2026-07-15): the add-on's cart
 * quantity is always exactly 1 per add-to-cart action — never
 * multiplied by the tier picker or Dawn's own quantity stepper. A
 * second, separately-submitted Wheelchair line (a genuinely new
 * add-to-cart action) can carry its own independent du'a/charge. This
 * is why the add-on request below always sends quantity:1 and why nothing
 * here reads the tier/quantity value at all.
 */
(function () {
  var checkbox = document.querySelector('[data-mr-fp-checkbox]');
  if (!checkbox) return;

  var field = document.querySelector('[data-mr-fp-field]');
  var textarea = document.querySelector('[data-mr-fp-textarea]');
  var counter = document.querySelector('[data-mr-fp-counter]');
  var pairInput = document.querySelector('[data-mr-fp-pair-id-wheelchair]');
  var form = checkbox.form;
  var addonVariantId = checkbox.getAttribute('data-mr-fp-addon-variant-id');

  var DUA_PROPERTY_NAME = "properties[Personalised Du'a]";
  var PAIR_PROPERTY_NAME = 'properties[_mr_fulfilment_addon_id]';

  function syncFieldVisibility() {
    var checked = checkbox.checked;
    if (field) {
      field.classList.toggle('mr-c-fulfilment-personalise__field--visible', checked);
    }
    if (textarea) {
      textarea.disabled = !checked;
      if (checked) {
        textarea.setAttribute('name', DUA_PROPERTY_NAME);
      } else {
        // Remove (not blank) the name attribute so an unchecked box submits
        // nothing here at all -- FormData only includes elements that HAVE
        // a name. .value is left untouched so re-checking restores exactly
        // what was typed, instead of silently discarding it.
        textarea.removeAttribute('name');
      }
    }
  }

  function updateCounter() {
    if (!counter || !textarea) return;
    var max = textarea.getAttribute('maxlength');
    if (!max) return;
    var len = textarea.value.length;
    counter.textContent = len + ' / ' + max;
    if (len >= parseInt(max, 10)) {
      counter.setAttribute('data-mr-fp-limit-reached', '');
    } else {
      counter.removeAttribute('data-mr-fp-limit-reached');
    }
  }

  checkbox.addEventListener('change', function () {
    syncFieldVisibility();
    updateCounter();
  });
  if (textarea) {
    textarea.addEventListener('input', updateCounter);
  }

  // Initial state (handles both the default-checked setting and a plain
  // page load) -- run once, no animation on first paint.
  syncFieldVisibility();
  updateCounter();

  if (!form || !addonVariantId) return;

  function generatePairId() {
    return 'mrfp_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  // Capture phase: runs before Dawn's own bubble-phase submit handler,
  // guaranteeing the properties[...] name/value state below is correct
  // by the time Dawn reads FormData(this.form) for the Wheelchair line.
  form.addEventListener(
    'submit',
    function () {
      if (!checkbox.checked) {
        if (pairInput) pairInput.removeAttribute('name');
        return;
      }
      var pairId = generatePairId();
      if (pairInput) {
        pairInput.setAttribute('name', PAIR_PROPERTY_NAME);
        pairInput.value = pairId;
      }
      fireAddonRequest(pairId);
    },
    true
  );

  function fireAddonRequest(pairId) {
    var body = {
      items: [
        {
          id: parseInt(addonVariantId, 10),
          quantity: 1,
          properties: {
            _mr_fulfilment_addon_id: pairId
          }
        }
      ]
    };
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('mr-fulfilment-personalise: add-on cart add failed, status ' + res.status);
        return res.json();
      })
      .then(function () {
        // Nudge any cart-count/drawer listener that follows the common
        // Dawn/Shopify convention, then directly refresh the header cart
        // count as a robust fallback regardless of whether anything was
        // listening for the event.
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        return fetch('/cart.js');
      })
      .then(function (res) {
        return res && res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function (err) {
        console.warn('mr-fulfilment-personalise:', err);
      });
  }
})();
